// Vox Meridian — expansion pack (Oct 2026)
// 25 new trade pages (24 previously unpaged trades + field machining), 3 marketing
// verticals, and India as the 9th country. Merged into the core data in site.js.
// `featured` slugs get per-country pages; `vertical` entries are marketing verticals
// spanning multiple trades. Everything else gets a global page only.

const IMG_A =
  'https://images.unsplash.com/photo-1652888510609-ed2d2ad64d6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxwcmVjaXNpb24lMjBtYWNoaW5pbmclMjB3b3Jrc2hvcHxlbnwwfHx8fDE3ODg5MTczMjJ8MA&ixlib=rb-4.1.0&q=85';
const IMG_B =
  'https://images.unsplash.com/photo-1716191299980-a6e8827ba10b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwZWxlY3RyaWNhbCUyMGF1dG9tYXRpb258ZW58MHx8fHwxNzg4OTE3MzIyfDA&ixlib=rb-4.1.0&q=85';
const IMG_C =
  'https://images.unsplash.com/photo-1544724569-5f546fd6f2b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwZWxlY3RyaWNhbCUyMGF1dG9tYXRpb258ZW58MHx8fHwxNzg4OTE3MzIyfDA&ixlib=rb-4.1.0&q=85';

export const INDIA = {
  code: 'IN',
  name: 'India',
  seoName: 'India',
  hubs: ['Chennai', 'Pune', 'Mumbai', 'Bengaluru', 'Ahmedabad', 'NCR'],
  blurb:
    'India runs one of the world’s broadest industrial bases — automotive around Chennai and Pune, chemicals and engineering in Gujarat, aerospace and electronics in Bengaluru, and heavy fabrication across the Mumbai–NCR corridors.',
};

// Slugs with full per-country pages (/services/<slug>/<country>). The 10 original
// trades plus field machining; verticals get country pages via their own flag.
export const FEATURED_SLUGS = [
  'precision-machining',
  'mro-services',
  'industrial-calibration',
  'industrial-electrical',
  'automation-integration',
  'metal-fabrication-welding',
  'pumps-motors-gearboxes',
  'hvac-industrial-refrigeration',
  'hydraulic-services',
  'industrial-piping',
  'field-machining',
];

// The ten trades shown in the homepage grid (layout is tuned for ten).
export const HOME_SLUGS = [
  'precision-machining',
  'mro-services',
  'industrial-calibration',
  'industrial-electrical',
  'automation-integration',
  'metal-fabrication-welding',
  'pumps-motors-gearboxes',
  'hvac-industrial-refrigeration',
  'hydraulic-services',
  'industrial-piping',
];

export const EXPANSION_SERVICE = {
  name: 'Field & on-site machining',
  examples: 'On-site milling, boring, flange facing, line boring',
};

export const EXPANSION_SERVICE_MAP = {
  'field-machining': 'Field & on-site machining',
};

export const EXPANSION_CATEGORIES = [
  {
    slug: 'mechanical-engineering',
    name: 'Mechanical engineering',
    keyword: 'mechanical engineering services',
    tagline: 'Machine modifications, alignment and installation — engineered, not improvised.',
    image: IMG_A,
    intro: [
      'Every plant eventually needs a machine modified, a line re-aligned, or a part that no longer exists reverse-engineered. Mechanical engineering services turn "we can’t buy it" into "we can make it" — but only when the calculations, tolerances and installation method are done properly.',
      'Post your RFQ with drawings, photos and constraints attached. Vox Meridian matches you with verified mechanical engineering providers in your country — firms that calculate before they cut.',
    ],
    servicesTitle: 'Mechanical scopes our providers quote on',
    services: [
      'Machinery modification and upgrades',
      'Precision shaft and laser alignment',
      'Installation, levelling and commissioning support',
      'Reverse engineering of obsolete components',
      'Vibration and failure analysis',
      'Mechanical design calculations and drawings',
    ],
    sections: [
      {
        h: 'How buyers use Vox Meridian for mechanical work',
        paragraphs: [
          'Attach the drawing, the photo of the worn part, or the sketch of the modification you need. The founder matches your job to three to five engineering firms in your country whose capability fits — targeting 24–48 hours, subject to availability.',
          'You receive comparable quotes against the same scope instead of three unrelated emails. Free for buyers during the pilot phase.',
        ],
      },
      {
        h: 'Why engineering firms join as providers',
        paragraphs: [
          'Good mechanical firms live off referrals — which means quiet weeks between project calls. Founding providers receive pre-qualified RFQs matched to their declared capability, with scope and deadline attached.',
          'The founding rate of SGD 29 per month is locked while subscribed for the first thirty providers. Verification is handled personally by the founder during this concierge phase.',
        ],
      },
    ],
  },
  {
    slug: 'pneumatic-services',
    name: 'Pneumatic services',
    keyword: 'pneumatic services',
    tagline: 'Air cylinders, valves and FRLs — leaks found, pressure restored.',
    image: IMG_B,
    intro: [
      'Pneumatics run quietly in the background of every production line — until a cylinder sticks, a valve chatters, or a leak taxes your compressor all night. Pneumatic services keep actuators, valves and air preparation units doing their job without drama.',
      'Post your RFQ with photos of the manifold or cylinder and the machine symptoms. We match you with verified pneumatic specialists in your country who carry the right seals, spools and fittings.',
    ],
    servicesTitle: 'Pneumatic scopes our providers quote on',
    services: [
      'Air cylinder repair and re-sealing',
      'Solenoid and process valve service',
      'FRL unit and air preparation maintenance',
      'Leak detection and compressed-air audits',
      'Pneumatic panel build and tubing',
      'Vacuum systems and ejectors',
    ],
    sections: [
      {
        h: 'For maintenance teams',
        paragraphs: [
          'Describe the symptom — slow stroke, pressure drop, air hissing — with photos of the pneumatic circuit. Matched providers quote repair or replacement with realistic turnaround, so downtime is measured in hours, not weeks.',
          'All providers are verified registered businesses. Posting an RFQ is free during the pilot phase.',
        ],
      },
      {
        h: 'For pneumatic specialists',
        paragraphs: [
          'Founding providers receive RFQs matched to their stock and skill — cylinders, valves, panels or full air audits — from plants that need the work done now.',
          'Lock SGD 29 per month while subscribed as a founding provider. No commission on jobs won — a flat rate and a steady flow of matched work.',
        ],
      },
    ],
  },
  {
    slug: 'air-compressor-services',
    name: 'Air compressor services',
    keyword: 'air compressor services',
    tagline: 'Compressor maintenance, dryers and leak detection — air you can rely on.',
    image: IMG_C,
    intro: [
      'Compressed air is the fourth utility — and the most wasted one. A poorly maintained compressor burns electricity, a failed dryer floods lines with water, and undetected leaks can eat a third of your air bill. Air compressor services keep the utility invisible, as it should be.',
      'Post your RFQ with your compressor make, model and symptoms. Vox Meridian matches you with verified compressor specialists in your country — service teams and overhaul workshops that know your machine.',
    ],
    servicesTitle: 'Compressed-air scopes our providers quote on',
    services: [
      'Preventive maintenance contracts',
      'Airend overhaul and rebuilds',
      'Air dryer and filter servicing',
      'Leak detection surveys',
      'Receiver and pressure vessel inspections',
      'Emergency breakdown callouts',
    ],
    sections: [
      {
        h: 'For facility and plant teams',
        paragraphs: [
          'Tell us the machine, the running hours and the fault — or the maintenance scope if you are planning ahead. Matched providers quote against the same scope with parts policy and warranty stated up front.',
          'Every provider is verified before quoting: business registration, insurance and relevant pressure-vessel credentials. Free for buyers during the pilot.',
        ],
      },
      {
        h: 'For compressor service companies',
        paragraphs: [
          'Founding providers receive RFQs matched to their service range — maintenance contracts, airend rebuilds, audits — from facilities with real budgets and deadlines.',
          'Lock the founding rate of SGD 29 per month while subscribed. Flat rate, no commission, no lead fees — a channel you can plan around.',
        ],
      },
    ],
  },
  {
    slug: 'boilers-steam-systems',
    name: 'Boilers & steam systems',
    keyword: 'boiler services',
    tagline: 'Boiler servicing, steam traps and insulation — efficiency you can meter.',
    image: IMG_C,
    intro: [
      'Steam is expensive to make and easy to waste. A failed steam trap can vent thousands a year, poor insulation heats the sky, and a neglected boiler fails its inspection at the worst possible time. Boiler and steam services keep the heat where you paid for it.',
      'Post your RFQ with boiler capacity, fuel type and the symptom or inspection scope. We match you with verified boiler specialists in your country — including statutory inspection support.',
    ],
    servicesTitle: 'Steam scopes our providers quote on',
    services: [
      'Boiler servicing, tuning and overhaul',
      'Steam trap surveys and replacement',
      'Burner service and combustion tuning',
      'Thermal insulation and cladding',
      'Statutory inspection preparation',
      'Condensate return system repairs',
    ],
    sections: [
      {
        h: 'For plant engineers',
        paragraphs: [
          'Attach photos of the nameplate, the trap stations or the lagging in question. Matched providers quote with test certificates and documentation your insurer expects — not just a number.',
          'Providers are verified for registration and the licences boiler work legally requires in your country. Free for buyers during the pilot.',
        ],
      },
      {
        h: 'For boiler and steam specialists',
        paragraphs: [
          'Founding providers receive RFQs matched to their certification class — servicing, inspections, trap surveys or insulation — from plants with live compliance dates.',
          'Lock SGD 29 per month while subscribed as one of the first thirty providers. Verification handled personally by the founder.',
        ],
      },
    ],
  },
  {
    slug: 'water-wastewater-treatment',
    name: 'Water & wastewater treatment',
    keyword: 'industrial wastewater treatment services',
    tagline: 'Treatment plants, filtration and testing — discharge you can defend.',
    image: IMG_B,
    intro: [
      'Industrial water is a compliance story: what comes in must be treated, and what goes out must pass. When a DAF underperforms, a membrane fouls or a discharge limit is breached, you need water and wastewater treatment services that respond with chemistry, not excuses.',
      'Post your RFQ with your process flow, flow rates and the parameter giving trouble. We match you with verified treatment specialists in your country — operators, maintenance teams and testing labs.',
    ],
    servicesTitle: 'Water scopes our providers quote on',
    services: [
      'Treatment plant operation and maintenance',
      'Filtration, RO and membrane servicing',
      'Chemical dosing system calibration',
      'Water and effluent testing',
      'Pump, blower and diffuser maintenance',
      'Sludge handling and removal',
    ],
    sections: [
      {
        h: 'For facility and EHS teams',
        paragraphs: [
          'Describe the plant, the parameter and the deadline — an audit, a consent limit, a production ramp. Matched providers quote with lab reports and service records your compliance file needs.',
          'Every provider is a verified registered business with relevant environmental credentials. Posting is free during the pilot.',
        ],
      },
      {
        h: 'For water treatment providers',
        paragraphs: [
          'Founding providers receive RFQs matched to their capability — plant O&M, testing, membrane service — from factories with compliance clocks ticking.',
          'Lock the founding rate of SGD 29 per month while subscribed. No commission — just matched work in your inbox.',
        ],
      },
    ],
  },
  {
    slug: 'industrial-cleaning',
    name: 'Industrial cleaning',
    keyword: 'industrial cleaning services',
    tagline: 'Machinery degreasing, tank cleaning and high-pressure washing — audit-ready sites.',
    image: IMG_A,
    intro: [
      'Industrial cleaning is not janitorial work — it is degreasing a line before a food audit, cleaning a tank before a product changeover, or hydro-blasting a yard before the client walk-through. Done wrong, it contaminates product and fails audits. Done right, nobody notices — which is the point.',
      'Post your RFQ with the asset, the contamination and the standard you must meet. We match you with verified industrial cleaning crews in your country.',
    ],
    servicesTitle: 'Cleaning scopes our providers quote on',
    services: [
      'Machinery and line degreasing',
      'Tank and vessel cleaning',
      'High-pressure and hydro-blasting',
      'Cleanroom and controlled-area cleaning',
      'Post-construction industrial cleans',
      'Oil spill response and remediation',
    ],
    sections: [
      {
        h: 'For plant and quality teams',
        paragraphs: [
          'State the standard — food-safe, audit prep, changeover — and the access constraints. Matched providers quote with method statements and the chemicals list your EHS team will ask for.',
          'All providers are verified, insured businesses. Free for buyers during the pilot phase.',
        ],
      },
      {
        h: 'For industrial cleaning contractors',
        paragraphs: [
          'Founding providers receive RFQs matched to their equipment and certifications — confined space, food-grade, high-pressure — from sites with real deadlines.',
          'Lock SGD 29 per month while subscribed as a founding provider. Flat rate, no commission.',
        ],
      },
    ],
  },
  {
    slug: 'waste-management-recycling',
    name: 'Waste management & recycling',
    keyword: 'industrial waste management services',
    tagline: 'Scrap, scheduled waste and recycling — documented from gate to disposal.',
    image: IMG_C,
    intro: [
      'Every factory produces waste, and every auditor wants the trail. Scheduled waste, scrap metal, used oil, packaging — industrial waste management services keep your site legal and your records clean, with consignment notes that survive inspections.',
      'Post your RFQ with waste streams, volumes and collection frequency. We match you with licensed waste contractors in your country.',
    ],
    servicesTitle: 'Waste scopes our providers quote on',
    services: [
      'Scheduled and hazardous waste collection',
      'Scrap metal and used oil recycling',
      'General industrial waste contracts',
      'Packaging and pallet recycling',
      'E-waste and secure destruction',
      'Waste audit and reduction programmes',
    ],
    sections: [
      {
        h: 'For EHS and facility teams',
        paragraphs: [
          'List the streams and volumes; photos of the storage area help. Matched providers quote with licences and consignment documentation stated up front — the paperwork is the product.',
          'Providers are verified for the waste licences your jurisdiction requires. Free for buyers during the pilot.',
        ],
      },
      {
        h: 'For waste contractors',
        paragraphs: [
          'Founding providers receive RFQs matched to their licence classes and truck capacity — recurring contracts, not one-off skips.',
          'Lock the founding rate of SGD 29 per month while subscribed. No commission on the contracts you win.',
        ],
      },
    ],
  },
  {
    slug: 'inspection-testing',
    name: 'Inspection & testing',
    keyword: 'industrial inspection and testing services',
    tagline: 'NDT, weld inspection and material testing — evidence, not opinion.',
    image: IMG_A,
    intro: [
      'When a weld must hold pressure or a shaft must not crack, hope is not a strategy. Inspection and testing services — NDT, weld inspection, material analysis — turn "probably fine" into certified evidence your client and insurer accept.',
      'Post your RFQ with the asset, the code you are working to, and the test methods required. We match you with verified inspection providers in your country, including accredited laboratories.',
    ],
    servicesTitle: 'Inspection scopes our providers quote on',
    services: [
      'Ultrasonic, MPI, DPI and radiographic testing',
      'Weld inspection and visual examination',
      'Positive material identification (PMI)',
      'Pressure testing and certification',
      'Thickness surveys and corrosion mapping',
      'Concrete and structural testing',
    ],
    sections: [
      {
        h: 'For QA and project engineers',
        paragraphs: [
          'State the code — ASME, AWS, API, EN — the extent of testing and the reporting format you need. Matched providers quote with technician certifications and sample reports up front.',
          'Providers are verified for accreditation and certification before they quote. Free for buyers during the pilot.',
        ],
      },
      {
        h: 'For inspection companies',
        paragraphs: [
          'Founding providers receive RFQs matched to their methods and certifications — shutdown windows, fabrication shops, site work.',
          'Lock SGD 29 per month while subscribed as one of the first thirty. The founder verifies every firm personally.',
        ],
      },
    ],
  },
  {
    slug: 'workplace-safety-services',
    name: 'Workplace safety services',
    keyword: 'workplace safety services',
    tagline: 'Risk assessments, safety audits and industrial hygiene — before the incident.',
    image: IMG_B,
    intro: [
      'Safety paperwork is the cheapest insurance a factory buys — and the first thing investigators ask for. Risk assessments, safety audits and industrial hygiene monitoring keep your people safe and your operation defensible.',
      'Post your RFQ with your headcount, processes and the audit or assessment you need. We match you with verified safety professionals in your country.',
    ],
    servicesTitle: 'Safety scopes our providers quote on',
    services: [
      'Risk assessments and HIRA/JHA',
      'Workplace safety audits and inspections',
      'Industrial hygiene monitoring (noise, dust, chemical)',
      'Safety management system setup',
      'Incident investigation support',
      'Safety training and toolbox programmes',
    ],
    sections: [
      {
        h: 'For plant managers and EHS leads',
        paragraphs: [
          'Tell us the standard you must meet — client audit, statutory inspection, certification — and the deadline. Matched providers quote with consultant credentials and sample deliverables.',
          'Every provider is verified for the registrations and competency schemes your country requires. Free during the pilot.',
        ],
      },
      {
        h: 'For safety consultants',
        paragraphs: [
          'Founding providers receive RFQs matched to their specialisations — audits, hygiene monitoring, training — from factories with real compliance dates.',
          'Lock the founding rate of SGD 29 per month while subscribed. Flat rate, no commission.',
        ],
      },
    ],
  },
  {
    slug: 'fire-protection-systems',
    name: 'Fire protection systems',
    keyword: 'fire protection services',
    tagline: 'Alarms, sprinklers and suppression — serviced, tested, certified.',
    image: IMG_C,
    intro: [
      'Fire systems spend their lives idle — and must work perfectly the one time they are called. Lapsed servicing is invisible until an insurer, an auditor or a fire finds it. Fire protection services keep alarms, sprinklers and suppression systems certified and ready.',
      'Post your RFQ with your system types, floor area and the service or installation scope. We match you with verified fire contractors in your country.',
    ],
    servicesTitle: 'Fire protection scopes our providers quote on',
    services: [
      'Fire alarm servicing and testing',
      'Sprinkler and hydrant system maintenance',
      'Suppression systems (kitchen, clean agent, CO2)',
      'Extinguisher supply and servicing',
      'Fire pump testing and overhaul',
      'Statutory inspection and certification support',
    ],
    sections: [
      {
        h: 'For facility managers',
        paragraphs: [
          'List the systems and the last service dates if you have them. Matched providers quote maintenance contracts and remedials with certification included — not as a surprise extra.',
          'Providers are verified for the fire-safety licences your country requires. Free for buyers during the pilot.',
        ],
      },
      {
        h: 'For fire protection contractors',
        paragraphs: [
          'Founding providers receive RFQs matched to their licence scope — servicing, installation, certification — recurring contracts from industrial sites.',
          'Lock SGD 29 per month while subscribed. Founding rate held while the subscription stays active.',
        ],
      },
    ],
  },
  {
    slug: 'lifting-equipment-services',
    name: 'Lifting equipment services',
    keyword: 'lifting equipment inspection services',
    tagline: 'Cranes, hoists and lifting gear — inspected, repaired, certified.',
    image: IMG_A,
    intro: [
      'A crane failure is never a small event. Statutory inspections, load testing and proper repairs keep lifting equipment legal and loads in the air. When certification lapses or a hoist starts slipping, lifting equipment services are not optional.',
      'Post your RFQ with equipment types, capacities and the inspection or repair scope. We match you with verified lifting specialists in your country.',
    ],
    servicesTitle: 'Lifting scopes our providers quote on',
    services: [
      'Statutory crane and hoist inspections',
      'Load testing and certification',
      'Crane, hoist and winch repairs',
      'Lifting gear inspection (slings, shackles, beams)',
      'Wire rope and chain replacement',
      'Overhead crane rail and festoon maintenance',
    ],
    sections: [
      {
        h: 'For plant and HSE teams',
        paragraphs: [
          'Tell us the equipment list and certification due dates. Matched providers quote with inspector accreditations and the documentation your statutory file requires.',
          'Providers are verified for competent-person registrations and insurance. Free for buyers during the pilot.',
        ],
      },
      {
        h: 'For lifting specialists',
        paragraphs: [
          'Founding providers receive RFQs matched to their inspection classes and repair capability — recurring statutory cycles that repeat every year.',
          'Lock the founding rate of SGD 29 per month while subscribed. Verification is personal, by the founder.',
        ],
      },
    ],
  },
  {
    slug: 'material-handling-equipment',
    name: 'Material handling equipment',
    keyword: 'material handling equipment services',
    tagline: 'Forklifts, conveyors and dock equipment — keep the flow moving.',
    image: IMG_B,
    intro: [
      'Warehouses and plants run on movement: forklifts lifting, conveyors rolling, dock levellers cycling hundreds of times a day. When a conveyor stalls or the only reach truck dies, the whole operation queues behind it. Material handling services keep the flow invisible.',
      'Post your RFQ with the equipment, the fault and your shift pattern. We match you with verified MHE service providers in your country.',
    ],
    servicesTitle: 'MHE scopes our providers quote on',
    services: [
      'Forklift servicing and repair',
      'Conveyor maintenance and belt splicing',
      'Dock leveller and door servicing',
      'Racking inspection and repair',
      'AGV and automation equipment support',
      'Battery and charger maintenance',
    ],
    sections: [
      {
        h: 'For warehouse and plant teams',
        paragraphs: [
          'Describe the fleet and the failure — nameplate photos speed everything. Matched providers quote call-outs and maintenance contracts with response times in writing.',
          'Every provider is a verified, insured business. Posting an RFQ is free during the pilot.',
        ],
      },
      {
        h: 'For MHE service companies',
        paragraphs: [
          'Founding providers receive RFQs matched to their equipment franchises and coverage area — recurring fleet contracts, not one-off repairs.',
          'Lock SGD 29 per month while subscribed as a founding provider. No commission on contracts won.',
        ],
      },
    ],
  },
  {
    slug: 'machinery-installation-relocation',
    name: 'Machinery installation & relocation',
    keyword: 'machinery relocation services',
    tagline: 'Rigging, factory moves and commissioning — heavy, precise, insured.',
    image: IMG_C,
    intro: [
      'Moving a 10-tonne machine is not a forklift job — it is rigging engineering, route surveys, method statements and insurance. Whether it is one machine across the hall or an entire line across the border, machinery installation and relocation specialists make heavy moves boring.',
      'Post your RFQ with machine weights, dimensions, origin and destination. We match you with verified rigging and relocation contractors in your country.',
    ],
    servicesTitle: 'Relocation scopes our providers quote on',
    services: [
      'Machine rigging and skidding',
      'Factory and line relocations',
      'Installation, levelling and anchoring',
      'Dismantling and match-marking',
      'Heavy haulage coordination',
      'Commissioning support',
    ],
    sections: [
      {
        h: 'For project and plant engineers',
        paragraphs: [
          'Attach the machine datasheet and photos of both sites — door heights and floor loading matter. Matched providers quote with method statements, lift plans and insurance certificates.',
          'All providers are verified registered businesses with rigging credentials. Free for buyers during the pilot.',
        ],
      },
      {
        h: 'For rigging and relocation contractors',
        paragraphs: [
          'Founding providers receive RFQs matched to their equipment and lift capacity — single machines to whole-plant moves.',
          'Lock the founding rate of SGD 29 per month while subscribed. Flat rate, no commission.',
        ],
      },
    ],
  },
  {
    slug: 'surface-treatment-coatings',
    name: 'Surface treatment & protective coatings',
    keyword: 'industrial coating services',
    tagline: 'Blasting, painting, plating and powder coating — protection that lasts.',
    image: IMG_A,
    intro: [
      'Corrosion never sleeps, and a cheap paint job announces itself within a year. Proper surface treatment — preparation grade, coating system, cure control — is the difference between five years of protection and one monsoon of rust.',
      'Post your RFQ with the substrate, environment and the specification or performance standard required. We match you with verified coating contractors in your country.',
    ],
    servicesTitle: 'Coating scopes our providers quote on',
    services: [
      'Abrasive blasting and surface preparation',
      'Industrial painting systems',
      'Powder coating',
      'Plating, anodising and galvanising coordination',
      'Tank and vessel linings',
      'Fireproofing and intumescent coatings',
    ],
    sections: [
      {
        h: 'For maintenance and project teams',
        paragraphs: [
          'State the environment — marine, chemical, food plant — and the standard (ISO 12944, SSPC) if you have one. Matched providers quote surface prep and DFT in the scope, so quotes are actually comparable.',
          'Providers are verified for registration, insurance and coating credentials. Free during the pilot.',
        ],
      },
      {
        h: 'For coating contractors',
        paragraphs: [
          'Founding providers receive RFQs matched to their plant capacity and applicator certifications — shop coating or site work.',
          'Lock SGD 29 per month while subscribed as one of the first thirty. No commission on work won.',
        ],
      },
    ],
  },
  {
    slug: 'tooling-moulds-dies',
    name: 'Tooling, moulds & dies',
    keyword: 'tool and die services',
    tagline: 'Mould repairs, die making, jigs and fixtures — precision that keeps presses running.',
    image: IMG_A,
    intro: [
      'When a mould flashes or a die cracks, production stops at full speed. Tooling, mould and die services — repair, maintenance, new builds — keep presses cycling and parts in tolerance, whether it is a worn ejector pin or a complete new fixture.',
      'Post your RFQ with the tool drawings, the defect and your production schedule. We match you with verified toolrooms in your country.',
    ],
    servicesTitle: 'Tooling scopes our providers quote on',
    services: [
      'Mould and die repair and refurbishment',
      'Preventive tooling maintenance programmes',
      'Jigs, fixtures and gauges',
      'New mould and die builds',
      'EDM and precision grinding work',
      'Texture, polish and engraving repairs',
    ],
    sections: [
      {
        h: 'For production and tooling engineers',
        paragraphs: [
          'Attach the tool drawing and photos of the defect or the part defect it causes. Matched toolrooms quote repair versus replace with honest lead times — so you can plan around the press schedule.',
          'Every provider is a verified business with proven toolroom capability. Free for buyers during the pilot.',
        ],
      },
      {
        h: 'For toolrooms and die shops',
        paragraphs: [
          'Founding providers receive RFQs matched to their machines and specialties — repairs, new builds, fixtures — from manufacturers with live production pressure.',
          'Lock the founding rate of SGD 29 per month while subscribed. The founder verifies every shop personally.',
        ],
      },
    ],
  },
  {
    slug: 'rubber-plastic-engineering',
    name: 'Rubber & plastic engineering',
    keyword: 'rubber and plastic engineering services',
    tagline: 'Roller relining, seals and custom components — the parts nobody stocks.',
    image: IMG_B,
    intro: [
      'Conveyor rollers, custom seals, UHMW guides, gasket oddities — rubber and plastic components fail on their own schedule and rarely have a part number. Rubber and plastic engineering services remake, reline and fabricate the parts that keep lines moving.',
      'Post your RFQ with dimensions, material and photos of the worn part. We match you with verified specialists in your country.',
    ],
    servicesTitle: 'Scopes our providers quote on',
    services: [
      'Roller relining and recovering',
      'Custom seals, gaskets and O-rings',
      'Plastic welding and fabrication',
      'UHMW, PTFE and nylon machining',
      'Rubber lining for tanks and chutes',
      'Prototype and short-run moulding',
    ],
    sections: [
      {
        h: 'For maintenance teams',
        paragraphs: [
          'Dimensions and a photo of the failed part are usually enough for a first quote — send the part itself for confirmation. Matched providers quote material options with honest wear-life expectations.',
          'All providers are verified registered businesses. Free for buyers during the pilot phase.',
        ],
      },
      {
        h: 'For rubber and plastic workshops',
        paragraphs: [
          'Founding providers receive RFQs matched to their processes — relining, machining, welding, lining — from plants that need parts yesterday.',
          'Lock SGD 29 per month while subscribed. Flat rate, no commission, no lead fees.',
        ],
      },
    ],
  },
  {
    slug: 'factory-building-maintenance',
    name: 'Factory building maintenance',
    keyword: 'factory building maintenance services',
    tagline: 'Floors, roofs, drainage and civil repairs — the building behind the business.',
    image: IMG_C,
    intro: [
      'Epoxy floors crack under forklifts, roofs leak onto machines, and drains block on the Friday before the audit. Factory building maintenance keeps the shell of your operation as reliable as the machines inside it.',
      'Post your RFQ with photos, approximate areas and access constraints. We match you with verified industrial building contractors in your country.',
    ],
    servicesTitle: 'Building scopes our providers quote on',
    services: [
      'Industrial flooring and epoxy repair',
      'Roof and gutter repairs',
      'Drainage and waterproofing',
      'Structural crack and concrete repair',
      'Cladding, shutter and dock repairs',
      'Painting and general civil works',
    ],
    sections: [
      {
        h: 'For facility and plant managers',
        paragraphs: [
          'Photos plus rough measurements get you comparable quotes. Matched contractors quote with method statements that respect your production schedule — night and weekend work where needed.',
          'Every provider is verified and insured before quoting. Free for buyers during the pilot.',
        ],
      },
      {
        h: 'For building maintenance contractors',
        paragraphs: [
          'Founding providers receive RFQs matched to their trades and crew size — from floor patches to full reroofs.',
          'Lock the founding rate of SGD 29 per month while subscribed. No commission on the work you win.',
        ],
      },
    ],
  },
  {
    slug: 'energy-efficiency-services',
    name: 'Energy efficiency services',
    keyword: 'industrial energy efficiency services',
    tagline: 'Energy audits, power quality and efficiency upgrades — savings you can meter.',
    image: IMG_B,
    intro: [
      'Energy is the bill nobody itemises. A proper audit finds the compressed-air leaks, the oversized motors and the power-factor penalties hiding in your utility costs — then efficiency upgrades pay for themselves on a schedule you can show the CFO.',
      'Post your RFQ with your utility spend and the scope — audit, power quality study, or a specific upgrade. We match you with verified energy specialists in your country.',
    ],
    servicesTitle: 'Energy scopes our providers quote on',
    services: [
      'Industrial energy audits',
      'Power quality and harmonic studies',
      'Power factor correction',
      'Compressed air and steam efficiency',
      'VSD retrofits and motor optimisation',
      'Solar feasibility and monitoring',
    ],
    sections: [
      {
        h: 'For plant and sustainability leads',
        paragraphs: [
          'Share your last utility bills and operating pattern. Matched providers quote audits with deliverables defined — findings, savings estimates, implementation costs — not a sales deck.',
          'Providers are verified for relevant energy-auditor credentials. Free for buyers during the pilot.',
        ],
      },
      {
        h: 'For energy service companies',
        paragraphs: [
          'Founding providers receive RFQs matched to their instruments and specialisations — audits, studies, retrofits — from manufacturers watching energy costs climb.',
          'Lock SGD 29 per month while subscribed as a founding provider. Flat rate, no commission.',
        ],
      },
    ],
  },
  {
    slug: 'engineering-design-consulting',
    name: 'Engineering design & consulting',
    keyword: 'engineering design services',
    tagline: 'CAD drafting, process design and project management — brains before steel.',
    image: IMG_B,
    intro: [
      'The cheapest engineering is done before anything is cut. Design and consulting services — drafting, process design, project management — turn a vague idea into drawings, BOMs and schedules that fabricators can actually price.',
      'Post your RFQ with the outcome you need: drawings, a design package, or someone to run the project. We match you with verified engineering consultants in your country.',
    ],
    servicesTitle: 'Design scopes our providers quote on',
    services: [
      'CAD drafting and 3D modelling',
      'Process and plant layout design',
      'Reverse engineering documentation',
      'Project management and owner’s engineering',
      'FEA and design verification',
      'As-built and drawing updates',
    ],
    sections: [
      {
        h: 'For project owners',
        paragraphs: [
          'Describe the deliverable — a fabrication drawing, a full design package, a PM for your shutdown. Matched consultants quote against defined deliverables and revision rounds, so scope creep dies at the quote stage.',
          'Every provider is a verified business or registered professional. Free for buyers during the pilot.',
        ],
      },
      {
        h: 'For engineering consultancies',
        paragraphs: [
          'Founding providers receive RFQs matched to their disciplines and software — drafting to full EPCM-lite packages.',
          'Lock the founding rate of SGD 29 per month while subscribed. The founder verifies every firm personally.',
        ],
      },
    ],
  },
  {
    slug: 'contract-manufacturing-assembly',
    name: 'Contract manufacturing & assembly',
    keyword: 'contract manufacturing services',
    tagline: 'Outsourced production, assembly and packing — capacity without headcount.',
    image: IMG_A,
    intro: [
      'Demand spikes, a customer wants local assembly, or your line simply has no spare hours — contract manufacturing gives you production capacity without payroll. The trick is finding a partner whose quality habits match your customer’s audit.',
      'Post your RFQ with drawings, volumes and the standard required. We match you with verified contract manufacturers in your country.',
    ],
    servicesTitle: 'Manufacturing scopes our providers quote on',
    services: [
      'Contract machining and fabrication runs',
      'Electromechanical assembly',
      'Sub-assembly and kitting',
      'Packing, labelling and fulfilment',
      'Overflow and surge production',
      'Prototype to pilot production',
    ],
    sections: [
      {
        h: 'For buyers and product owners',
        paragraphs: [
          'Attach the drawing pack, target volumes and quality requirements. Matched manufacturers quote piece price, tooling assumptions and lead time in one comparable format — capacity questions answered up front.',
          'Providers are verified for registration and production capability. Free for buyers during the pilot.',
        ],
      },
      {
        h: 'For contract manufacturers',
        paragraphs: [
          'Founding providers receive RFQs matched to their processes and spare capacity — recurring production work, not one-off samples.',
          'Lock SGD 29 per month while subscribed as one of the first thirty. No commission on the contracts you win.',
        ],
      },
    ],
  },
  {
    slug: 'industrial-it-networking',
    name: 'Industrial IT & networking',
    keyword: 'industrial networking services',
    tagline: 'Factory networks, industrial PCs and OT cybersecurity — the plant’s nervous system.',
    image: IMG_B,
    intro: [
      'When the SCADA drops or a machine loses its network, production stops and IT says "that’s OT, not us." Industrial IT and networking services live in that gap — plant-floor networks, industrial computers, and the cybersecurity that keeps OT and IT safely separated.',
      'Post your RFQ with your current setup and the problem or project. We match you with verified industrial IT specialists in your country.',
    ],
    servicesTitle: 'Industrial IT scopes our providers quote on',
    services: [
      'Plant network design and installation',
      'Industrial PC and HMI replacement',
      'OT cybersecurity assessments',
      'Machine data collection setup',
      'Fibre, wireless and radio links',
      'UPS and panel power for control systems',
    ],
    sections: [
      {
        h: 'For plant and IT managers',
        paragraphs: [
          'Describe the machines, the network and the symptom or project. Matched providers quote with OT experience stated — the difference between a network that survives a factory and one that does not.',
          'Every provider is a verified business with industrial references. Free for buyers during the pilot.',
        ],
      },
      {
        h: 'For industrial IT providers',
        paragraphs: [
          'Founding providers receive RFQs matched to their stack and specialisations — networking, cybersecurity, data — from manufacturers digitising their floors.',
          'Lock the founding rate of SGD 29 per month while subscribed. Flat rate, no commission.',
        ],
      },
    ],
  },
  {
    slug: 'technical-manpower-training',
    name: 'Technical manpower & training',
    keyword: 'technical manpower services',
    tagline: 'Shutdown crews, contract technicians and operator training — hands when you need them.',
    image: IMG_C,
    intro: [
      'A turnaround needs forty fitters for two weeks. A new line needs operators trained before handover. Technical manpower and training services supply vetted hands and structured skills — without a permanent headcount you cannot justify.',
      'Post your RFQ with trades, headcount, duration and site requirements. We match you with verified manpower and training providers in your country.',
    ],
    servicesTitle: 'Manpower scopes our providers quote on',
    services: [
      'Shutdown and turnaround crews',
      'Contract maintenance technicians',
      'Commissioning support teams',
      'Operator and technician training',
      'Safety and trade certification courses',
      'Skills assessment and competency mapping',
    ],
    sections: [
      {
        h: 'For maintenance and HR managers',
        paragraphs: [
          'State the trades, the dates and the certifications required. Matched providers quote with crew CVs, certification evidence and mobilisation plans — not just a day rate.',
          'Providers are verified for registration and insurance. Free for buyers during the pilot.',
        ],
      },
      {
        h: 'For manpower and training companies',
        paragraphs: [
          'Founding providers receive RFQs matched to their crew trades and course catalogue — shutdowns, contracts, training programmes.',
          'Lock SGD 29 per month while subscribed. No commission — a flat rate and matched demand.',
        ],
      },
    ],
  },
  {
    slug: 'industrial-logistics-warehousing',
    name: 'Industrial logistics & warehousing',
    keyword: 'industrial logistics services',
    tagline: 'Heavy transport, freight forwarding and storage — moved, cleared, kept.',
    image: IMG_C,
    intro: [
      'A machine that clears customs late or arrives damaged is a project delay with a shipping label. Industrial logistics services — heavy transport, forwarding, warehousing — move industrial cargo with the rigging plans, permits and paperwork it actually needs.',
      'Post your RFQ with cargo dimensions, weight, origin and destination. We match you with verified industrial logistics providers in your country.',
    ],
    servicesTitle: 'Logistics scopes our providers quote on',
    services: [
      'Heavy and oversized transport',
      'Freight forwarding and customs clearance',
      'Industrial warehousing and storage',
      'Crane and rigging support for loading',
      'Machine export packing and crating',
      'Project cargo coordination',
    ],
    sections: [
      {
        h: 'For project and plant teams',
        paragraphs: [
          'Dimensions, weight and photos of the cargo get you real quotes — plus the permits and escort requirements stated, not discovered mid-route. Matched providers quote door-to-door with responsibilities clear.',
          'Every provider is a verified, insured business. Free for buyers during the pilot.',
        ],
      },
      {
        h: 'For logistics providers',
        paragraphs: [
          'Founding providers receive RFQs matched to their fleet, licences and warehouse capacity — heavy haulage to long-term storage.',
          'Lock the founding rate of SGD 29 per month while subscribed. The founder verifies every provider personally.',
        ],
      },
    ],
  },
  {
    slug: 'marine-offshore-services',
    name: 'Marine & offshore services',
    keyword: 'marine and offshore services',
    tagline: 'Vessel repairs, marine electrical and offshore maintenance — seaworthy work.',
    image: IMG_C,
    intro: [
      'Marine work forgives nothing: classification rules, port windows, and a vessel that earns nothing at anchor. Marine and offshore services — repairs, electrical, riding crews — keep vessels classed, crewed and moving.',
      'Post your RFQ with vessel or equipment details, location and the port window. We match you with verified marine contractors in your country.',
    ],
    servicesTitle: 'Marine scopes our providers quote on',
    services: [
      'Vessel mechanical and hull repairs',
      'Marine electrical and automation work',
      'Riding crew and voyage repairs',
      'Offshore platform maintenance support',
      'Drydock preparation and supervision',
      'Marine fabrication and pipework',
    ],
    sections: [
      {
        h: 'For vessel managers and marine superintendents',
        paragraphs: [
          'State the vessel, the port window and the class requirements. Matched contractors quote with class-society familiarity and mobilisation times in writing — because the window does not move.',
          'Providers are verified for registration, insurance and marine credentials. Free for buyers during the pilot.',
        ],
      },
      {
        h: 'For marine contractors',
        paragraphs: [
          'Founding providers receive RFQs matched to their trades and port coverage — repair calls, riding crews, offshore campaigns.',
          'Lock SGD 29 per month while subscribed as a founding provider. No commission on the work you win.',
        ],
      },
    ],
  },
  {
    slug: 'field-machining',
    name: 'Field & on-site machining',
    keyword: 'field machining services',
    tagline: 'On-site milling, boring and flange facing — the machine comes to the breakdown.',
    image: IMG_A,
    intro: [
      'Some components are too big, too welded-in, or too critical to move. Field machining brings the workshop to the asset: portable mills, line borers and flange facers that restore surfaces in place — saving the crane hire, the transport and weeks of downtime.',
      'Post your RFQ with the component, dimensions, material and site conditions. We match you with verified field machining specialists in your country who mobilise with their own equipment.',
    ],
    servicesTitle: 'Field machining scopes our providers quote on',
    services: [
      'On-site milling and surface restoration',
      'Line boring of bores and housings',
      'Flange facing and re-surfacing',
      'Journal and shaft machining in situ',
      'Stud removal and thread repair on-site',
      'Drilling, tapping and cold cutting',
    ],
    sections: [
      {
        h: 'For plant and shutdown engineers',
        paragraphs: [
          'Describe the component and the damage — photos with a tape measure in frame help more than a paragraph. Matched specialists quote mobilisation, machining time and tolerance achievable on-site, so the decision is repair-in-place versus remove, with numbers.',
          'Every provider is a verified business with proven on-site capability. Free for buyers during the pilot phase.',
        ],
      },
      {
        h: 'For field machining specialists',
        paragraphs: [
          'Your equipment is mobile and your rates are earned in emergencies — the problem is being found at 2 a.m. Founding providers receive RFQs matched to their kit list and mobilisation range.',
          'Lock the founding rate of SGD 29 per month while subscribed, held for as long as the subscription stays active. Verification is personal, by the founder.',
        ],
      },
    ],
  },
];

// Marketing verticals — buyer-problem pages spanning several trades. They get
// per-country pages like featured trades but never appear in the homepage grid.
// Data-centre M&E and automation retrofit were promoted to full sectors in
// site-sectors.js (2026-10-27); only the calibration programme remains here.
const ALL_VERTICALS = [
  {
    slug: 'data-centre-mep',
    name: 'Data Centre M&E Services',
    vertical: true,
    keyword: 'data centre M&E services',
    tagline: 'Cooling, power, piping and commissioning for mission-critical builds.',
    image: IMG_B,
    intro: [
      'A data centre is an industrial plant wearing a clean shirt: megawatts of cooling, power distribution, fire suppression and piping, all with uptime stakes that tolerate no amateurs. During construction the race is fit-out and commissioning; after handover it is maintenance that never sleeps.',
      'Vox Meridian routes data-centre RFQs to verified M&E providers — chiller and cooling specialists, electrical contractors, piping crews, fire protection and commissioning teams — matched to your trade and country, with the founder reviewing every match during this concierge phase.',
    ],
    servicesTitle: 'Data-centre scopes our providers quote on',
    services: [
      'Chillers, CRAH/CRAC and cooling systems',
      'Electrical fit-out, switchgear and UPS',
      'Process and chilled-water piping',
      'Fire protection and suppression systems',
      'Commissioning and testing support',
      'Ongoing M&E maintenance contracts',
    ],
    sections: [
      {
        h: 'For main contractors, consultants and operators',
        paragraphs: [
          'Post the package scope with drawings or the equipment schedule attached. The founder matches you with three to five verified M&E providers in your country whose certifications fit mission-critical work — targeting 24–48 hours, and honest if the match needs more sourcing time.',
          'For operators, the same channel covers the maintenance phase: cooling overhauls, switchgear testing, fire-system servicing — comparable quotes without the procurement chase.',
        ],
      },
      {
        h: 'For M&E subcontractors and service firms',
        paragraphs: [
          'Data-centre work is growing fast, but breaking in means knowing the right consultant or main contractor. Founding providers receive matched RFQs from live projects and operating facilities — scope, budget band and deadline attached.',
          'The founding rate of SGD 29 per month is locked while subscribed for the first thirty providers. Verification — registry, licences, track record — is handled personally by the founder.',
        ],
      },
    ],
  },
  {
    slug: 'automation-retrofit',
    name: 'Automation Retrofit Programmes',
    vertical: true,
    keyword: 'factory automation retrofit',
    tagline: 'Retrofit the machines you have — OEE up, without a new line.',
    image: IMG_B,
    intro: [
      'Most factories cannot buy their way to automation — they retrofit their way there. A twenty-year-old press with a new PLC, sensors and an OEE dashboard outperforms a shiny purchase order that arrives in nine months. Retrofit programmes upgrade controls, add data collection and de-risk the whole journey in stages.',
      'Post your retrofit scope — the machines, the current controls, the outcome you need. Vox Meridian matches you with verified retrofit integrators in your country who have modernised equipment like yours before.',
    ],
    servicesTitle: 'Retrofit scopes our providers quote on',
    services: [
      'PLC and controls modernisation',
      'Sensor and instrumentation retrofits',
      'OEE and machine data dashboards',
      'Safety system upgrades to current standards',
      'Servo and drive conversions',
      'Robotic cell add-ons to existing lines',
    ],
    sections: [
      {
        h: 'For production and operations managers',
        paragraphs: [
          'Tell us the machines, the pain — downtime, scrap, no data — and the budget band. Matched integrators quote staged approaches: what to retrofit first, what it costs, what it returns. You compare plans, not just prices.',
          'Every integrator is verified for registration and real retrofit references. Free for buyers during the pilot phase.',
        ],
      },
      {
        h: 'For retrofit integrators',
        paragraphs: [
          'Founding providers receive RFQs from manufacturers with real budgets and named machines — no speculative "what would automation cost" enquiries.',
          'Lock the founding rate of SGD 29 per month while subscribed. State your platforms once; see only RFQs that fit your wheelhouse.',
        ],
      },
    ],
  },
  {
    slug: 'calibration-compliance',
    name: 'Calibration & Compliance Programmes',
    shortName: 'Calibration Compliance',
    vertical: true,
    keyword: 'calibration compliance programme',
    tagline: 'Audit calendars, recall schedules and certificates — compliance that never slips.',
    image: IMG_A,
    intro: [
      'Nobody fails an ISO audit on purpose — they fail it on a forgotten caliper. A calibration compliance programme turns a spreadsheet of instruments and dates into a managed system: recall scheduling, accredited certificates, and an audit file that assembles itself.',
      'Post your instrument list and the standard you answer to — ISO 9001, 13485, AS9100, IATF. Vox Meridian matches you with verified calibration providers in your country, including accredited laboratories, who run the programme with you.',
    ],
    servicesTitle: 'Programme scopes our providers quote on',
    services: [
      'Calibration recall scheduling and management',
      'On-site and in-lab calibration contracts',
      'Audit file preparation and certificate management',
      'Instrument register setup and review',
      'Measurement uncertainty and MSA support',
      'Multi-site calibration coordination',
    ],
    sections: [
      {
        h: 'For quality managers',
        paragraphs: [
          'Attach your instrument register — make, model, range, criticality. Matched providers quote the programme, not just the per-certificate price: turnaround commitments, recall reminders, audit support. Your next audit stops being a scramble.',
          'Every provider is verified for accreditation scope before they quote. Free for buyers during the pilot phase.',
        ],
      },
      {
        h: 'For calibration providers',
        paragraphs: [
          'Programmes are the recurring-revenue work every lab wants. Founding providers receive RFQs from quality managers with live audit dates and real instrument lists — matched to your accreditation scope.',
          'Lock the founding rate of SGD 29 per month while subscribed. No commission — the contracts you win are yours.',
        ],
      },
    ],
  },
];

// Only calibration-compliance remains a cross-service focus area; the other two
// former verticals are now full sectors with their own sub-service pages.
export const VERTICALS = ALL_VERTICALS.filter((v) => v.slug === 'calibration-compliance');

// Directory links: service name → page slug, for every new page above.
export const EXPANSION_PAGE_SLUGS = {
  'Mechanical engineering': 'mechanical-engineering',
  'Pneumatic services': 'pneumatic-services',
  'Air compressor services': 'air-compressor-services',
  'Boilers & steam systems': 'boilers-steam-systems',
  'Water & wastewater treatment': 'water-wastewater-treatment',
  'Industrial cleaning': 'industrial-cleaning',
  'Waste management & recycling': 'waste-management-recycling',
  'Inspection & testing': 'inspection-testing',
  'Workplace safety services': 'workplace-safety-services',
  'Fire protection systems': 'fire-protection-systems',
  'Lifting equipment services': 'lifting-equipment-services',
  'Material handling equipment': 'material-handling-equipment',
  'Machinery installation & relocation': 'machinery-installation-relocation',
  'Surface treatment & protective coatings': 'surface-treatment-coatings',
  'Tooling, moulds & dies': 'tooling-moulds-dies',
  'Rubber & plastic engineering': 'rubber-plastic-engineering',
  'Factory building maintenance': 'factory-building-maintenance',
  'Energy efficiency services': 'energy-efficiency-services',
  'Engineering design & consulting': 'engineering-design-consulting',
  'Contract manufacturing & assembly': 'contract-manufacturing-assembly',
  'Industrial IT & networking': 'industrial-it-networking',
  'Technical manpower & training': 'technical-manpower-training',
  'Industrial logistics & warehousing': 'industrial-logistics-warehousing',
  'Marine & offshore services': 'marine-offshore-services',
  'Field & on-site machining': 'field-machining',
};
